import React from 'react';
import './App.css';

class Base extends React.Component {
    constructor(props) {
        super(props);
        
        // Maze dimensions
        const mazeWidth = 20;
        const mazeHeight = 20;
        
        // Generate maze
        const maze = this.generateMaze(mazeWidth, mazeHeight);
        
        // Generate random gems
        const gems = this.generateGems(mazeWidth, mazeHeight);
        console.log('Generated gems:', gems.length, gems);
        
        this.state = {
            maze: maze,
            mazeWidth: mazeWidth,
            mazeHeight: mazeHeight,
            playerX: 0,
            playerY: 0,
            playerPosX: 0, // Interpolated position for smooth movement
            playerPosY: 0, // Interpolated position for smooth movement
            playerDirection: 'down', // 'up', 'down', 'left', 'right'
            cameraX: 0,
            cameraY: 0,
            cameraTargetX: 0,
            cameraTargetY: 0,
            keys: {
                up: false,
                down: false,
                left: false,
                right: false
            },
            tileSize: 40, // Size of each tile in isometric view
            cellSize: 120, // Size of each cell in pixels (2x scale)
            wallHeight: 30,
            lightRotation: 0, // Rotation angle for the rotating light
            gems: gems // Array of {x, y} gem positions
        };
        
        this.animationFrameId = null;
    }
    
    // Generate maze using recursive backtracking
    generateMaze(width, height) {
        // Initialize maze with all walls
        const maze = Array(height).fill(null).map(() => 
            Array(width).fill(null).map(() => ({
                top: true,
                right: true,
                bottom: true,
                left: true,
                visited: false
            }))
        );
        
        // Stack for backtracking
        const stack = [];
        let current = { x: 0, y: 0 };
        maze[current.y][current.x].visited = true;
        stack.push(current);
        
        // Directions: up, right, down, left
        const directions = [
            { x: 0, y: -1, wall: 'top', opposite: 'bottom' },
            { x: 1, y: 0, wall: 'right', opposite: 'left' },
            { x: 0, y: 1, wall: 'bottom', opposite: 'top' },
            { x: -1, y: 0, wall: 'left', opposite: 'right' }
        ];
        
        while (stack.length > 0) {
            const neighbors = [];
            
            // Find unvisited neighbors
            for (const dir of directions) {
                const nx = current.x + dir.x;
                const ny = current.y + dir.y;
                
                if (nx >= 0 && nx < width && ny >= 0 && ny < height && !maze[ny][nx].visited) {
                    neighbors.push({ dir, x: nx, y: ny });
                }
            }
            
            if (neighbors.length > 0) {
                // Choose random neighbor
                const next = neighbors[Math.floor(Math.random() * neighbors.length)];
                
                // Remove walls between current and next
                maze[current.y][current.x][next.dir.wall] = false;
                maze[next.y][next.x][next.dir.opposite] = false;
                
                // Mark next as visited and push to stack
                maze[next.y][next.x].visited = true;
                stack.push(current);
                current = { x: next.x, y: next.y };
            } else {
                // Backtrack
                current = stack.pop();
            }
        }
        
        // Create entrance and exit
        maze[0][0].left = false; // Entrance
        maze[height - 1][width - 1].right = false; // Exit
        
        return maze;
    }
    
    // Generate random gems in the maze
    generateGems(width, height) {
        const numGems = Math.floor(Math.random() * 15) + 10; // 10-24 gems
        const gems = [];
        const usedPositions = new Set();
        
        for (let i = 0; i < numGems; i++) {
            let x, y, key;
            let attempts = 0;
            const maxAttempts = 100;
            
            // Try to find a valid position (not at start, not duplicate)
            do {
                x = Math.floor(Math.random() * width);
                y = Math.floor(Math.random() * height);
                key = `${x},${y}`;
                attempts++;
            } while (((x === 0 && y === 0) || usedPositions.has(key)) && attempts < maxAttempts);
            
            if (attempts < maxAttempts && !(x === 0 && y === 0) && !usedPositions.has(key)) {
                gems.push({ x, y });
                usedPositions.add(key);
            }
        }
        
        return gems;
    }
    
    // Convert grid coordinates to isometric screen coordinates
    gridToIso(gridX, gridY) {
        const { cellSize } = this.state;
        const isoX = (gridX - gridY) * (cellSize / 2);
        const isoY = (gridX + gridY) * (cellSize / 4);
        return { x: isoX, y: isoY };
    }
    
    // Convert isometric screen coordinates to grid coordinates
    isoToGrid(isoX, isoY) {
        const { cellSize } = this.state;
        const gridX = Math.round((isoX / (cellSize / 2) + isoY / (cellSize / 4)) / 2);
        const gridY = Math.round((isoY / (cellSize / 4) - isoX / (cellSize / 2)) / 2);
        return { x: gridX, y: gridY };
    }
    
    // Check if player can move to a cell
    canMoveTo(fromX, fromY, toX, toY) {
        const { maze, mazeWidth, mazeHeight } = this.state;
        
        if (toX < 0 || toX >= mazeWidth || toY < 0 || toY >= mazeHeight) {
            return false;
        }
        
        const cell = maze[fromY][fromX];
        
        // Check direction
        if (toX > fromX && cell.right) return false; // Moving right, check right wall
        if (toX < fromX && cell.left) return false; // Moving left, check left wall
        if (toY > fromY && cell.bottom) return false; // Moving down, check bottom wall
        if (toY < fromY && cell.top) return false; // Moving up, check top wall
        
        return true;
    }
    
    handleKeyDown = (event) => {
        // Prevent default to avoid scrolling
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'W', 'w', 'S', 's', 'A', 'a', 'D', 'd'].includes(event.key)) {
            event.preventDefault();
        }
        
        const { playerX, playerY, keys } = this.state;
        let newX = playerX;
        let newY = playerY;
        let newDirection = this.state.playerDirection;
        let moved = false;
        
        // Only move if key wasn't already pressed (prevent repeat movement while holding)
        if (event.key === 'ArrowUp' || event.key === 'W' || event.key === 'w') {
            if (!keys.up && this.canMoveTo(playerX, playerY, playerX, playerY - 1)) {
                newY = playerY - 1;
                newDirection = 'up';
                moved = true;
            }
            this.setState({ keys: { ...keys, up: true } });
        }
        if (event.key === 'ArrowDown' || event.key === 'S' || event.key === 's') {
            if (!keys.down && this.canMoveTo(playerX, playerY, playerX, playerY + 1)) {
                newY = playerY + 1;
                newDirection = 'down';
                moved = true;
            }
            this.setState({ keys: { ...keys, down: true } });
        }
        if (event.key === 'ArrowLeft' || event.key === 'A' || event.key === 'a') {
            if (!keys.left && this.canMoveTo(playerX, playerY, playerX - 1, playerY)) {
                newX = playerX - 1;
                newDirection = 'left';
                moved = true;
            }
            this.setState({ keys: { ...keys, left: true } });
        }
        if (event.key === 'ArrowRight' || event.key === 'D' || event.key === 'd') {
            if (!keys.right && this.canMoveTo(playerX, playerY, playerX + 1, playerY)) {
                newX = playerX + 1;
                newDirection = 'right';
                moved = true;
            }
            this.setState({ keys: { ...keys, right: true } });
        }
        
        if (moved) {
            // Check for gem collection
            const { gems } = this.state;
            const remainingGems = gems.filter(gem => !(gem.x === newX && gem.y === newY));
            const collectedGem = gems.find(gem => gem.x === newX && gem.y === newY);
            
            if (collectedGem && this.props.onScoreUpdate) {
                // Add 20 to score
                this.props.onScoreUpdate(20);
            }
            
            // Update camera target to follow player
            const playerIso = this.gridToIso(newX, newY);
            const centerX = window.innerWidth / 2;
            const centerY = window.innerHeight / 2;
            
            this.setState({
                playerX: newX,
                playerY: newY,
                playerDirection: newDirection,
                cameraTargetX: centerX - playerIso.x,
                cameraTargetY: centerY - playerIso.y,
                gems: remainingGems
            });
        }
    }
    
    handleKeyUp = (event) => {
        const { keys } = this.state;
        let newKeys = { ...keys };
        
        if (event.key === 'ArrowUp' || event.key === 'W' || event.key === 'w') {
            newKeys.up = false;
        }
        if (event.key === 'ArrowDown' || event.key === 'S' || event.key === 's') {
            newKeys.down = false;
        }
        if (event.key === 'ArrowLeft' || event.key === 'A' || event.key === 'a') {
            newKeys.left = false;
        }
        if (event.key === 'ArrowRight' || event.key === 'D' || event.key === 'd') {
            newKeys.right = false;
        }
        
        this.setState({ keys: newKeys });
    }
    
    updatePlayer() {
        // Smoothly interpolate camera toward target
        const { cameraX, cameraY, cameraTargetX, cameraTargetY, lightRotation, playerPosX, playerPosY, playerX, playerY } = this.state;
        const lerpFactor = 0.15; // Smoothing factor for camera (0-1, lower = smoother but slower)
        const playerLerpFactor = 0.2; // Smoothing factor for player movement (higher = faster movement)
        
        const newCameraX = cameraX + (cameraTargetX - cameraX) * lerpFactor;
        const newCameraY = cameraY + (cameraTargetY - cameraY) * lerpFactor;
        
        // Smoothly interpolate player position toward target
        const newPlayerPosX = playerPosX + (playerX - playerPosX) * playerLerpFactor;
        const newPlayerPosY = playerPosY + (playerY - playerPosY) * playerLerpFactor;
        
        // Rotate the light (always update)
        const newLightRotation = (lightRotation + 2) % 360;
        
        // Update camera if there's a meaningful change, but always update light rotation and player position
        const cameraChanged = Math.abs(newCameraX - cameraX) > 0.1 || Math.abs(newCameraY - cameraY) > 0.1;
        const playerChanged = Math.abs(newPlayerPosX - playerPosX) > 0.001 || Math.abs(newPlayerPosY - playerPosY) > 0.001;
        
        if (cameraChanged || playerChanged || newLightRotation !== lightRotation) {
            this.setState({
                cameraX: newCameraX,
                cameraY: newCameraY,
                playerPosX: newPlayerPosX,
                playerPosY: newPlayerPosY,
                lightRotation: newLightRotation
            });
        }
    }
    
    animationFrame = () => {
        this.updatePlayer();
        
        this.animationFrameId = requestAnimationFrame(this.animationFrame);
    }
    
    componentDidMount() {
        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('keyup', this.handleKeyUp);
        
        // Center camera on player initially
        const playerIso = this.gridToIso(this.state.playerX, this.state.playerY);
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2;
        
        this.setState({
            playerPosX: this.state.playerX,
            playerPosY: this.state.playerY,
            cameraX: centerX - playerIso.x,
            cameraY: centerY - playerIso.y,
            cameraTargetX: centerX - playerIso.x,
            cameraTargetY: centerY - playerIso.y
        });
        
        this.animationFrameId = requestAnimationFrame(this.animationFrame);
    }
    
    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('keyup', this.handleKeyUp);
        
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }
    }
    
    render() {
        const { maze, mazeWidth, mazeHeight, playerX, playerY, cameraX, cameraY, cellSize, wallHeight } = this.state;
        
        // Calculate visible range
        const viewWidth = window.innerWidth;
        const viewHeight = window.innerHeight;
        const padding = cellSize * 2;
        
        const tiles = [];
        const backWalls = []; // Top and left walls (behind player)
        const frontWalls = []; // Bottom and right walls (in front of player)
        
        // Render all cells (back to front for proper depth)
        for (let y = 0; y < mazeHeight; y++) {
            for (let x = 0; x < mazeWidth; x++) {
                const cell = maze[y][x];
                const iso = this.gridToIso(x, y);
                const screenX = iso.x + cameraX;
                const screenY = iso.y + cameraY;
                
                // Skip if outside viewport
                if (screenX < -padding || screenX > viewWidth + padding ||
                    screenY < -padding || screenY > viewHeight + padding) {
                    continue;
                }
                
                // Floor tile
                tiles.push(
                    <g key={`floor-${x}-${y}`} transform={`translate(${screenX}, ${screenY})`}>
                        {/* Floor */}
                        <polygon
                            points={`0,${cellSize / 4} ${cellSize / 2},0 ${cellSize},${cellSize / 4} ${cellSize / 2},${cellSize / 2}`}
                            fill="#333333"
                        />
                    </g>
                );
                
                // Top wall (north edge - top of diamond) - BACK WALL
                if (cell.top) {
                    backWalls.push(
                        <g key={`wall-top-${x}-${y}`} transform={`translate(${screenX}, ${screenY})`}>
                            <polygon
                                points={`${cellSize / 2},0 ${cellSize},${cellSize / 4} ${cellSize},${cellSize / 4 - wallHeight} ${cellSize / 2},-${wallHeight}`}
                                fill="#666666"
                                stroke="#888888"
                                strokeWidth="1"
                            />
                        </g>
                    );
                }
                
                // Left wall (west edge - left side of diamond) - BACK WALL
                if (cell.left) {
                    backWalls.push(
                        <g key={`wall-left-${x}-${y}`} transform={`translate(${screenX}, ${screenY})`}>
                            <polygon
                                points={`0,${cellSize / 4} ${cellSize / 2},0 ${cellSize / 2},-${wallHeight} 0,${cellSize / 4 - wallHeight}`}
                                fill="#555555"
                                stroke="#777777"
                                strokeWidth="1"
                            />
                        </g>
                    );
                }
                
                // Right wall (east edge - right side of diamond) - FRONT WALL
                if (cell.right) {
                    frontWalls.push(
                        <g key={`wall-right-${x}-${y}`} transform={`translate(${screenX}, ${screenY})`}>
                            <polygon
                                points={`${cellSize},${cellSize / 4} ${cellSize / 2},${cellSize / 2} ${cellSize / 2},${cellSize / 2 - wallHeight} ${cellSize},${cellSize / 4 - wallHeight}`}
                                fill="#555555"
                                stroke="#777777"
                                strokeWidth="1"
                            />
                        </g>
                    );
                }
                
                // Bottom wall (south edge - bottom of diamond) - FRONT WALL
                if (cell.bottom) {
                    frontWalls.push(
                        <g key={`wall-bottom-${x}-${y}`} transform={`translate(${screenX}, ${screenY})`}>
                            <polygon
                                points={`${cellSize / 2},${cellSize / 2} 0,${cellSize / 4} 0,${cellSize / 4 - wallHeight} ${cellSize / 2},${cellSize / 2 - wallHeight}`}
                                fill="#666666"
                                stroke="#888888"
                                strokeWidth="1"
                            />
                        </g>
                    );
                }
            }
        }
        
        // Player position in isometric (use interpolated position for smooth movement)
        const playerIso = this.gridToIso(this.state.playerPosX, this.state.playerPosY);
        const playerScreenX = playerIso.x + cameraX;
        const playerScreenY = playerIso.y + cameraY;
        
        // Sphere radius: 1.3x wall height
        const sphereRadius = wallHeight * 1.3 / 2;
        
        return (
            <div 
                className="base-view"
                style={{
                    width: '100%',
                    height: '100vh',
                    backgroundColor: '#1a1a1a',
                    overflow: 'hidden',
                    position: 'relative'
                }}
                tabIndex="0"
            >
                <svg
                    width="100%"
                    height="100%"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0
                    }}
                >
                    {tiles}
                    {backWalls}
                    
                    {/* Player Sphere */}
                    <g transform={`translate(${playerScreenX}, ${playerScreenY})`}>
                        {/* Main sphere */}
                        <circle
                            cx={cellSize / 2}
                            cy={cellSize / 4}
                            r={sphereRadius}
                            fill="#ffff00"
                            stroke="#cccc00"
                            strokeWidth="2"
                        />
                        
                        {/* Antennas on top */}
                        <line
                            x1={cellSize / 2 - sphereRadius * 0.3}
                            y1={cellSize / 4 - sphereRadius}
                            x2={cellSize / 2 - sphereRadius * 0.3}
                            y2={cellSize / 4 - sphereRadius - 8}
                            stroke="#888888"
                            strokeWidth="2"
                        />
                        <line
                            x1={cellSize / 2}
                            y1={cellSize / 4 - sphereRadius}
                            x2={cellSize / 2}
                            y2={cellSize / 4 - sphereRadius - 10}
                            stroke="#888888"
                            strokeWidth="2"
                        />
                        <line
                            x1={cellSize / 2 + sphereRadius * 0.3}
                            y1={cellSize / 4 - sphereRadius}
                            x2={cellSize / 2 + sphereRadius * 0.3}
                            y2={cellSize / 4 - sphereRadius - 8}
                            stroke="#888888"
                            strokeWidth="2"
                        />
                        
                        {/* Rotating white light around top */}
                        <g transform={`translate(${cellSize / 2}, ${cellSize / 4 - sphereRadius}) rotate(${this.state.lightRotation})`}>
                            <circle
                                cx="0"
                                cy="0"
                                r="4"
                                fill="#ffffff"
                                stroke="#ffffff"
                                strokeWidth="1"
                                opacity="0.9"
                            />
                            {/* Glow effect */}
                            <circle
                                cx="0"
                                cy="0"
                                r="6"
                                fill="#ffffff"
                                opacity="0.3"
                            />
                        </g>
                    </g>
                    
                    {/* Gems - rendered before front walls so they can be obscured */}
                    {this.state.gems.map((gem, index) => {
                        const gemIso = this.gridToIso(gem.x, gem.y);
                        const gemScreenX = gemIso.x + cameraX;
                        const gemScreenY = gemIso.y + cameraY;
                        const gemSize = 18; // Increased size for visibility
                        
                        return (
                            <g key={`gem-${index}`} transform={`translate(${gemScreenX}, ${gemScreenY})`}>
                                {/* Diamond shape in isometric view */}
                                <polygon
                                    points={`${cellSize / 2},${cellSize / 4 - gemSize} ${cellSize / 2 + gemSize},${cellSize / 4} ${cellSize / 2},${cellSize / 4 + gemSize} ${cellSize / 2 - gemSize},${cellSize / 4}`}
                                    fill="#00ffff"
                                    stroke="#0088ff"
                                    strokeWidth="2.5"
                                />
                                {/* Highlight on top */}
                                <polygon
                                    points={`${cellSize / 2},${cellSize / 4 - gemSize} ${cellSize / 2 + gemSize * 0.6},${cellSize / 4 - gemSize * 0.3} ${cellSize / 2},${cellSize / 4}`}
                                    fill="#ffffff"
                                    opacity="0.7"
                                />
                            </g>
                        );
                    })}
                    
                    {frontWalls}
                </svg>
                
                {/* Instructions overlay */}
                <div style={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    border: '2px solid #ffffff',
                    borderRadius: '8px',
                    padding: '15px',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    color: '#00ff00',
                    zIndex: 1000
                }}>
                    <div style={{ marginBottom: '10px', fontWeight: 'bold', fontSize: '16px' }}>BASE EXPLORATION</div>
                    <div>WASD / Arrow Keys: Move</div>
                    <div style={{ marginTop: '5px', fontSize: '12px', color: '#888888' }}>
                        Position: ({playerX}, {playerY})
                    </div>
                </div>
                
                {/* 2D Minimap */}
                <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    border: '2px solid #ffffff',
                    borderRadius: '8px',
                    padding: '10px',
                    zIndex: 1000,
                    width: '200px',
                    height: '200px'
                }}>
                    <div style={{
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        color: '#00ff00',
                        marginBottom: '5px',
                        fontWeight: 'bold'
                    }}>
                        MAP
                    </div>
                    <svg
                        width="180"
                        height="180"
                        viewBox={`-0.1 -0.1 ${mazeWidth + 0.2} ${mazeHeight + 0.2}`}
                        preserveAspectRatio="xMidYMid meet"
                        style={{
                            border: '1px solid #333333',
                            backgroundColor: '#000000'
                        }}
                    >
                        {/* Render maze cells */}
                        {maze.map((row, y) =>
                            row.map((cell, x) => {
                                return (
                                    <g key={`map-cell-${x}-${y}`}>
                                        {/* Cell background */}
                                        <rect
                                            x={x}
                                            y={y}
                                            width={1}
                                            height={1}
                                            fill="#222222"
                                            stroke="#333333"
                                            strokeWidth="0.02"
                                        />
                                        {/* Walls */}
                                        {cell.top && (
                                            <line
                                                x1={x}
                                                y1={y}
                                                x2={x + 1}
                                                y2={y}
                                                stroke="#666666"
                                                strokeWidth="0.05"
                                            />
                                        )}
                                        {cell.right && (
                                            <line
                                                x1={x + 1}
                                                y1={y}
                                                x2={x + 1}
                                                y2={y + 1}
                                                stroke="#666666"
                                                strokeWidth="0.05"
                                            />
                                        )}
                                        {cell.bottom && (
                                            <line
                                                x1={x}
                                                y1={y + 1}
                                                x2={x + 1}
                                                y2={y + 1}
                                                stroke="#666666"
                                                strokeWidth="0.05"
                                            />
                                        )}
                                        {cell.left && (
                                            <line
                                                x1={x}
                                                y1={y}
                                                x2={x}
                                                y2={y + 1}
                                                stroke="#666666"
                                                strokeWidth="0.05"
                                            />
                                        )}
                                    </g>
                                );
                            })
                        )}
                        
                        {/* Gems on minimap */}
                        {this.state.gems.map((gem, index) => (
                            <circle
                                key={`map-gem-${index}`}
                                cx={gem.x + 0.5}
                                cy={gem.y + 0.5}
                                r={0.15}
                                fill="#00ffff"
                                stroke="#0088ff"
                                strokeWidth="0.02"
                            />
                        ))}
                        
                        {/* Player position */}
                        <circle
                            cx={playerX + 0.5}
                            cy={playerY + 0.5}
                            r={0.25}
                            fill="#00ff00"
                            stroke="#ffffff"
                            strokeWidth="0.05"
                        />
                    </svg>
                </div>
                
                {/* Score display */}
                <div 
                    style={{
                        position: 'absolute',
                        bottom: '20px',
                        right: '20px',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        border: '2px solid #ffffff',
                        borderRadius: '8px',
                        padding: '8px 20px',
                        fontFamily: 'monospace',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#00ff00',
                        textShadow: '0 0 5px rgba(0, 255, 0, 0.8)',
                        boxShadow: '0 0 10px rgba(0, 255, 0, 0.3)',
                        zIndex: 1000,
                        pointerEvents: 'none'
                    }}
                    className="score-display">
                    SCORE: {(this.props.score || 0).toLocaleString()}
                </div>
            </div>
        );
    }
}

export default Base;

